import { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { documentsApi } from '../api/documents';
import type { DocumentItem, DocumentAnnotation } from '../types';

// react-pdf v10 worker setup — Vite hashes the worker file via the URL import.
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

interface DocumentViewerProps {
  document: DocumentItem;
  onClose: () => void;
  onRenamed?: (updated: DocumentItem) => void;
}

const ZOOM_STEPS = [0.5, 0.75, 1.0, 1.25, 1.5, 2.0];

export default function DocumentViewer({ document: doc, onClose, onRenamed }: DocumentViewerProps) {
  const queryClient = useQueryClient();
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [blobError, setBlobError] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [zoomIdx, setZoomIdx] = useState<number>(2); // 1.0
  const [pinMode, setPinMode] = useState<boolean>(false);
  const [draftPin, setDraftPin] = useState<{ x_pct: number; y_pct: number; body: string } | null>(null);
  const [editingAnnId, setEditingAnnId] = useState<number | null>(null);
  const [editingBody, setEditingBody] = useState<string>('');
  const [renaming, setRenaming] = useState<boolean>(false);
  const [renameValue, setRenameValue] = useState<string>(doc.filename);
  const [renameError, setRenameError] = useState<string | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);

  const isPdf = doc.mime_type === 'application/pdf';
  const isImage = !!doc.mime_type && doc.mime_type.startsWith('image/');

  // Load the file as a blob URL for inline display
  useEffect(() => {
    let cancelled = false;
    let createdUrl: string | null = null;
    setBlobUrl(null);
    setBlobError(null);
    documentsApi
      .getViewBlobUrl(doc.id)
      .then((url) => {
        if (cancelled) {
          window.URL.revokeObjectURL(url);
        } else {
          createdUrl = url;
          setBlobUrl(url);
        }
      })
      .catch(() => {
        if (!cancelled) setBlobError('Datei konnte nicht geladen werden.');
      });
    return () => {
      cancelled = true;
      if (createdUrl) window.URL.revokeObjectURL(createdUrl);
    };
  }, [doc.id]);

  // Memoize the file prop so react-pdf doesn't refetch on every render
  const pdfFile = useMemo(() => (blobUrl ? { url: blobUrl } : null), [blobUrl]);

  const annotationsKey = ['document-annotations', doc.id];
  const { data: annotations = [] } = useQuery({
    queryKey: annotationsKey,
    queryFn: () => documentsApi.getAnnotations(doc.id),
  });

  const createMut = useMutation({
    mutationFn: (a: { page_number: number | null; x_pct: number; y_pct: number; body: string }) =>
      documentsApi.createAnnotation(doc.id, a),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: annotationsKey });
      setDraftPin(null);
      setPinMode(false);
    },
  });

  const updateMut = useMutation({
    mutationFn: ({ id, body }: { id: number; body: string }) => documentsApi.updateAnnotation(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: annotationsKey });
      setEditingAnnId(null);
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => documentsApi.deleteAnnotation(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: annotationsKey }),
  });

  const renameMut = useMutation({
    mutationFn: (filename: string) => documentsApi.renameDocument(doc.id, filename),
    onSuccess: (updated) => {
      setRenaming(false);
      setRenameError(null);
      onRenamed?.(updated);
    },
    onError: () => setRenameError('Umbenennen fehlgeschlagen.'),
  });

  const currentPageForFilter = isImage ? null : pageNumber;
  const pageAnnotations = annotations.filter((a) =>
    isImage ? a.page_number === null : a.page_number === currentPageForFilter
  );

  const handleCanvasClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!pinMode) return;
    const target = e.currentTarget;
    const rect = target.getBoundingClientRect();
    const x_pct = (e.clientX - rect.left) / rect.width;
    const y_pct = (e.clientY - rect.top) / rect.height;
    setDraftPin({ x_pct, y_pct, body: '' });
  };

  const saveDraftPin = () => {
    if (!draftPin || !draftPin.body.trim()) return;
    createMut.mutate({
      page_number: currentPageForFilter,
      x_pct: draftPin.x_pct,
      y_pct: draftPin.y_pct,
      body: draftPin.body.trim(),
    });
  };

  const startEdit = (a: DocumentAnnotation) => {
    setEditingAnnId(a.id);
    setEditingBody(a.body);
  };

  const saveEdit = () => {
    if (!editingAnnId || !editingBody.trim()) return;
    updateMut.mutate({ id: editingAnnId, body: editingBody.trim() });
  };

  const submitRename = () => {
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed === doc.filename) {
      setRenaming(false);
      setRenameValue(doc.filename);
      return;
    }
    renameMut.mutate(trimmed);
  };

  const handleDownload = () => documentsApi.downloadDocument(doc.id, doc.filename);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full h-full max-w-7xl flex flex-col">
        {/* Toolbar */}
        <div className="flex items-center gap-3 px-4 py-2 border-b">
          {renaming ? (
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <input
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitRename();
                  if (e.key === 'Escape') {
                    setRenaming(false);
                    setRenameValue(doc.filename);
                  }
                }}
                autoFocus
                className="flex-1 border rounded px-2 py-1 text-sm"
              />
              <button onClick={submitRename} disabled={renameMut.isPending} className="text-sm text-blue-600 hover:text-blue-800">
                Speichern
              </button>
              <button
                onClick={() => {
                  setRenaming(false);
                  setRenameValue(doc.filename);
                  setRenameError(null);
                }}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Abbrechen
              </button>
              {renameError && <span className="text-xs text-red-600">{renameError}</span>}
            </div>
          ) : (
            <button
              onClick={() => setRenaming(true)}
              className="font-medium text-sm truncate flex-1 text-left hover:underline"
              title="Klicken zum Umbenennen"
            >
              {doc.filename}
            </button>
          )}

          {isPdf && numPages > 0 && (
            <div className="flex items-center gap-1 text-sm">
              <button
                onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                disabled={pageNumber <= 1}
                className="px-2 py-0.5 border rounded hover:bg-gray-50 disabled:opacity-50"
              >
                ‹
              </button>
              <span className="px-1">
                {pageNumber} / {numPages}
              </span>
              <button
                onClick={() => setPageNumber((p) => Math.min(numPages, p + 1))}
                disabled={pageNumber >= numPages}
                className="px-2 py-0.5 border rounded hover:bg-gray-50 disabled:opacity-50"
              >
                ›
              </button>
            </div>
          )}

          <div className="flex items-center gap-1 text-sm">
            <button
              onClick={() => setZoomIdx((i) => Math.max(0, i - 1))}
              disabled={zoomIdx <= 0}
              className="px-2 py-0.5 border rounded hover:bg-gray-50 disabled:opacity-50"
            >
              −
            </button>
            <span className="px-1 w-12 text-center">{Math.round(ZOOM_STEPS[zoomIdx] * 100)}%</span>
            <button
              onClick={() => setZoomIdx((i) => Math.min(ZOOM_STEPS.length - 1, i + 1))}
              disabled={zoomIdx >= ZOOM_STEPS.length - 1}
              className="px-2 py-0.5 border rounded hover:bg-gray-50 disabled:opacity-50"
            >
              +
            </button>
          </div>

          <button
            onClick={() => setPinMode((v) => !v)}
            className={`text-sm px-3 py-1 rounded border ${
              pinMode ? 'bg-blue-600 text-white border-blue-600' : 'bg-white hover:bg-gray-50'
            }`}
          >
            {pinMode ? 'Pin-Modus aktiv' : 'Kommentar hinzufügen'}
          </button>

          <button onClick={handleDownload} className="text-sm px-3 py-1 border rounded hover:bg-gray-50">
            Herunterladen
          </button>

          <button onClick={onClose} className="text-sm px-3 py-1 border rounded hover:bg-gray-50">
            Schließen
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 flex min-h-0">
          {/* Canvas */}
          <div className="flex-1 overflow-auto bg-gray-100 p-4 flex justify-center items-start">
            {blobError && <p className="text-red-600 text-sm">{blobError}</p>}
            {!blobUrl && !blobError && (
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mt-12" />
            )}
            {blobUrl && (
              <div
                ref={canvasRef}
                onClick={handleCanvasClick}
                className={`relative inline-block ${pinMode ? 'cursor-crosshair' : ''}`}
                style={{ lineHeight: 0 }}
              >
                {isPdf && pdfFile && (
                  <Document
                    file={pdfFile}
                    onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                    loading={<div className="text-sm text-gray-500 p-8">Lade PDF...</div>}
                    error={<div className="text-sm text-red-600 p-8">PDF konnte nicht geladen werden.</div>}
                  >
                    <Page
                      pageNumber={pageNumber}
                      scale={ZOOM_STEPS[zoomIdx]}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  </Document>
                )}
                {isImage && (
                  <img
                    src={blobUrl}
                    alt={doc.filename}
                    style={{ maxWidth: 'none', width: `${ZOOM_STEPS[zoomIdx] * 100}%` }}
                    draggable={false}
                  />
                )}
                {!isPdf && !isImage && (
                  <div className="text-sm text-gray-600 p-8 bg-white rounded">
                    Vorschau für diesen Dateityp nicht verfügbar. Bitte herunterladen.
                  </div>
                )}

                {/* Pins for current page */}
                {pageAnnotations.map((a, i) => (
                  <div
                    key={a.id}
                    className="absolute -translate-x-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-yellow-400 border-2 border-yellow-600 text-xs font-bold flex items-center justify-center shadow cursor-pointer hover:bg-yellow-500"
                    style={{ left: `${a.x_pct * 100}%`, top: `${a.y_pct * 100}%`, lineHeight: 'normal' }}
                    title={`${a.user_name || 'Unbekannt'}: ${a.body}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      startEdit(a);
                    }}
                  >
                    {i + 1}
                  </div>
                ))}

                {/* Draft pin */}
                {draftPin && (
                  <div
                    className="absolute -translate-x-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-red-400 border-2 border-red-600 shadow"
                    style={{ left: `${draftPin.x_pct * 100}%`, top: `${draftPin.y_pct * 100}%` }}
                  />
                )}
              </div>
            )}
          </div>

          {/* Comments panel */}
          <div className="w-80 border-l flex flex-col bg-white">
            <div className="px-4 py-2 border-b text-sm font-medium">
              Kommentare {isImage ? '' : `(Seite ${pageNumber})`}
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-3">
              {draftPin && (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-2 space-y-2">
                  <div className="text-xs text-gray-600">Neuer Kommentar</div>
                  <textarea
                    value={draftPin.body}
                    onChange={(e) => setDraftPin({ ...draftPin, body: e.target.value })}
                    autoFocus
                    rows={3}
                    className="w-full border rounded px-2 py-1 text-sm"
                    placeholder="Kommentar..."
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={saveDraftPin}
                      disabled={!draftPin.body.trim() || createMut.isPending}
                      className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 disabled:opacity-50"
                    >
                      Speichern
                    </button>
                    <button
                      onClick={() => setDraftPin(null)}
                      className="text-xs text-gray-600 hover:text-gray-800"
                    >
                      Abbrechen
                    </button>
                  </div>
                </div>
              )}

              {pageAnnotations.length === 0 && !draftPin && (
                <p className="text-xs text-gray-500">Noch keine Kommentare.</p>
              )}

              {pageAnnotations.map((a, i) => (
                <div key={a.id} className="border rounded p-2 text-sm">
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                    <span>
                      <span className="inline-block w-5 h-5 rounded-full bg-yellow-400 border border-yellow-600 text-xs font-bold text-center mr-1">
                        {i + 1}
                      </span>
                      {a.user_name || 'Unbekannt'}
                    </span>
                    <span>{new Date(a.created_at).toLocaleDateString('de-DE')}</span>
                  </div>
                  {editingAnnId === a.id ? (
                    <div className="space-y-2">
                      <textarea
                        value={editingBody}
                        onChange={(e) => setEditingBody(e.target.value)}
                        rows={3}
                        className="w-full border rounded px-2 py-1 text-sm"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={saveEdit}
                          disabled={!editingBody.trim() || updateMut.isPending}
                          className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700 disabled:opacity-50"
                        >
                          Speichern
                        </button>
                        <button
                          onClick={() => setEditingAnnId(null)}
                          className="text-xs text-gray-600 hover:text-gray-800"
                        >
                          Abbrechen
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm text-gray-800 whitespace-pre-wrap">{a.body}</p>
                      <div className="flex gap-3 mt-1">
                        <button
                          onClick={() => startEdit(a)}
                          className="text-xs text-gray-500 hover:text-gray-700"
                        >
                          Bearbeiten
                        </button>
                        <button
                          onClick={() => {
                            if (confirm('Kommentar wirklich löschen?')) deleteMut.mutate(a.id);
                          }}
                          className="text-xs text-red-500 hover:text-red-700"
                        >
                          Löschen
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
